/**
 * Recognising "you are being rate limited" across every shape it reaches the client in.
 *
 * There is no single shape to match. The limiter now lives in a Cloudflare rate limiting rule, and
 * Cloudflare answers with its own error document rather than the service's; the service itself may
 * still produce a Boom error; and apisauce hands the caller whichever of those it managed to parse.
 * Getting this wrong is not cosmetic - the user sees "we could not find an appointment" and assumes
 * the business is fully booked, instead of being told to wait.
 *
 * Shapes seen in practice:
 *
 *   Cloudflare JSON   { status: 429, error_code: 1015, error_category: 'rate_limit',
 *                       error_name: 'rate_limited', cloudflare_error: true, retry_after: 30 }
 *   Cloudflare HTML   '<!DOCTYPE html>... Error 1015: You are being rate limited ...'
 *   hapi / Boom       { statusCode: 429, error: 'Too Many Requests', message: '...' }
 *   axios-ish         { status: 429 } or { response: { status: 429 } }
 *   bare status       429
 *
 * Note the Cloudflare body uses `status`, the Boom body uses `statusCode`. Checking only the latter
 * is exactly what made a real 429 render as a generic "no appointments" error.
 *
 * Deliberately not surfacing Cloudflare's `retry_after`: it reports 30 seconds regardless of how
 * long the rule actually blocks for, and ours blocks for an hour. Telling a user to come back in
 * half a minute when they cannot would be worse than saying nothing.
 */

// 429 is what the Cloudflare rule returns. 503 is what the service's own in-process limiter used to
// return before it was removed; kept so a client running a cached bundle, or any future limiter
// answering 503, still gets the right message.
export const RATE_LIMIT_STATUS_CODES = [429, 503];

// Cloudflare's "You are being rate limited" error: error_code in its JSON body, "Error 1015" in HTML.
export const CLOUDFLARE_RATE_LIMIT_ERROR_CODE = 1015;

const RATE_LIMIT_TEXT = /\b1015\b|rate[\s_-]?limit/i;

const numericStatusOf = (value) => {
    const asNumber = typeof value === 'string' ? parseInt(value, 10) : value;
    return Number.isFinite(asNumber) ? asNumber : null;
};

const hasRateLimitStatus = (value) => RATE_LIMIT_STATUS_CODES.includes(numericStatusOf(value));

/**
 * @param {*} error anything an API call surfaced as its failure - object, string, status, or nothing
 * @returns {Boolean} whether it means the caller is being rate limited
 */
export const isRateLimited = (error) => {

    if (!error) {
        return false;
    }

    // A bare status code, e.g. from a caller that only kept response.status.
    if (typeof error === 'number') {
        return hasRateLimitStatus(error);
    }

    // An unparsed body - Cloudflare serves HTML unless JSON is negotiated.
    if (typeof error === 'string') {
        return RATE_LIMIT_TEXT.test(error);
    }

    if (typeof error !== 'object') {
        return false;
    }

    // statusCode: Boom. status: Cloudflare and axios. response.status: a raw axios error.
    const statuses = [error.statusCode, error.status, (error.response || {}).status];
    if (statuses.some(hasRateLimitStatus)) {
        return true;
    }

    if (numericStatusOf(error.error_code) === CLOUDFLARE_RATE_LIMIT_ERROR_CODE) {
        return true;
    }

    if (RATE_LIMIT_TEXT.test(String(error.error_category || error.error_name || ''))) {
        return true;
    }

    // apisauce keeps the parsed body on `data`, and our API layer sometimes passes the wrapper
    // through rather than the body. One level down is enough; deeper means something else is wrong.
    if (error.data && error.data !== error) {
        return isRateLimited(error.data);
    }

    return false;
};
